import React, { Fragment, useMemo, useState } from "react";
import { appPath, APP_PATHS } from '../../utils/appPaths.js';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Col, Container, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Image } from "../../AbstractElements";
import logoWhite from "../../assets/images/logo/logo-white.png";
import AuthService from "../../Services/auth/AuthService";
import TailChase from "../../Components/Loaders/TailChase";
import { toUserMessage } from "../../utils/apiErrorMessage";

const ResetPwd = ({ logoClassMain }) => {
  const [togglePassword, setTogglePassword] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [rePasswordValue, setRePasswordValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const fromQuery = searchParams.get("token");
    if (fromQuery) return fromQuery.trim();
    // HashRouter fallback: /#/reset-pwd?token=... may land in location.hash
    if (typeof window !== "undefined" && window.location.hash.includes("?")) {
      const hashQuery = window.location.hash.split("?")[1] || "";
      return new URLSearchParams(hashQuery).get("token")?.trim() || "";
    }
    return "";
  }, [searchParams]);

  const handleResetPwd = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error("Missing or invalid reset token. Request a new password reset link.");
      return;
    }
    if (passwordValue.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (passwordValue !== rePasswordValue) {
      toast.error("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const { success, message } = await AuthService.resetPassword(
        token,
        passwordValue,
        rePasswordValue
      );
      if (success) {
        toast.success(toUserMessage(message, "Password has been reset successfully."));
        navigate(APP_PATHS.login, { replace: true });
      } else {
        toast.error(
          toUserMessage(
            message,
            "Failed to reset password. The link may be invalid or expired."
          )
        );
      }
    } catch (error) {
      toast.error(
        toUserMessage(error, "Failed to reset password. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <section>
        <Container className="p-0 login-page" fluid={true}>
          <Row className="m-0">
            <Col className="p-0">
              <div className="login-card login-card--mono">
                <div>
                  <div>
                    <Link
                      className={`logo ${logoClassMain ? logoClassMain : ""}`}
                      to={APP_PATHS.login}
                    >
                      <Image
                        attrImage={{
                          className: "img-fluid for-light login-logo-white",
                          src: logoWhite,
                          alt: "reddiamond.",
                        }}
                      />
                    </Link>
                  </div>
                  <div className="login-main">
                    <Form
                      className="theme-form login-form"
                      onSubmit={handleResetPwd}
                    >
                      <h4 className="mb-3">Reset Your Password</h4>
                      <h6 className="mt-2 mb-3">Create Your Password</h6>

                      {!token && (
                        <p className="mb-3 text-danger">
                          This reset link is missing a token.{" "}
                          <Link to={APP_PATHS.forgetPwd} className="link">
                            Request a new one
                          </Link>
                          .
                        </p>
                      )}

                      <FormGroup className="position-relative">
                        <Label className="col-form-label m-0">
                          New Password
                        </Label>
                        <div className="position-relative">
                          <Input
                            className="form-control"
                            type={togglePassword ? "text" : "password"}
                            name="password"
                            autoComplete="new-password"
                            required
                            minLength={8}
                            placeholder="*********"
                            value={passwordValue}
                            onChange={(e) => setPasswordValue(e.target.value)}
                          />
                          <div
                            className="show-hide"
                            onClick={() => setTogglePassword(!togglePassword)}
                          >
                            <span className={togglePassword ? "" : "show"} />
                          </div>
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <Label className="col-form-label m-0">
                          Retype Password
                        </Label>
                        <Input
                          className="form-control"
                          type="password"
                          name="password_confirmation"
                          autoComplete="new-password"
                          required
                          minLength={8}
                          placeholder="*********"
                          value={rePasswordValue}
                          onChange={(e) => setRePasswordValue(e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <button
                          type="submit"
                          className="btn btn-login-mono d-block w-100"
                          disabled={loading || !token}
                        >
                          {loading ? <TailChase /> : "Done"}
                        </button>
                      </FormGroup>
                      <p className="text-start mb-0">
                        Already have a password?
                        <Link to={appPath('/login')} className="ms-2 link">
                          Sign in
                        </Link>
                      </p>
                    </Form>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <ToastContainer position="top-right" autoClose={4500} newestOnTop />
    </Fragment>
  );
};

export default ResetPwd;
